import { useMemo } from "react";
import type { OnboardingStep } from "../onboarding.types";

export type UseOnboardingStepperArgs = {
  /** Program ID */
  programId: number;
  /** Steps from useOnboardingActions */
  steps: OnboardingStep[];
  /** Current step by program from Zustand */
  currentStepByProgram: Record<number, number>;
  /** Skipped steps by program from Zustand */
  skippedStepsByProgram: Record<number, number[]>;
  /** URL step param (if any) */
  urlStep: string | null;
};

export type UseOnboardingStepperResult = {
  /** The effective current step index (from URL or Zustand) */
  effectiveStepIndex: number;
  /** Array of skipped step indices */
  skippedSteps: number[];
  /** The current step object */
  currentStep: OnboardingStep | null;
  /** Whether this is the last step */
  isLastStep: boolean;
  /** Whether the current step can be skipped */
  canSkip: boolean;
  /** Computed state for each step */
  stepStates: StepState[];
};

export type StepState = {
  /** Step index */
  index: number;
  /** Step data */
  step: OnboardingStep;
  /** Whether this step is completed */
  isCompleted: boolean;
  /** Whether this is the current step */
  isCurrent: boolean;
  /** Whether this step was skipped */
  isSkipped: boolean;
  /** Whether this step is clickable */
  isClickable: boolean;
};

/**
 * Hook that computes stepper state from onboarding data and store state.
 *
 * This hook handles the logic of determining which step is current,
 * which steps are skipped, and the clickable state of each step.
 *
 * @example
 * ```tsx
 * const {
 *   effectiveStepIndex,
 *   skippedSteps,
 *   currentStep,
 *   isLastStep,
 *   canSkip,
 *   stepStates,
 * } = useOnboardingStepper({
 *   programId,
 *   steps,
 *   currentStepByProgram,
 *   skippedStepsByProgram,
 *   urlStep,
 * });
 * ```
 */
export function useOnboardingStepper({
  programId,
  steps,
  currentStepByProgram,
  skippedStepsByProgram,
  urlStep,
}: UseOnboardingStepperArgs): UseOnboardingStepperResult {
  // Get current step from URL or Zustand
  const zustandStep = currentStepByProgram[programId] ?? 0;
  const effectiveStepIndex = urlStep ? parseInt(urlStep, 10) : zustandStep;

  // Get skipped steps from Zustand
  const skippedSteps = skippedStepsByProgram[programId] ?? [];

  // Current step object
  const currentStep = steps[effectiveStepIndex] ?? null;

  // Whether this is the last step
  const isLastStep = effectiveStepIndex >= steps.length - 1;

  // Whether current step can be skipped (non-required steps only)
  const canSkip = currentStep ? !currentStep.required : false;

  // Compute state for each step
  const stepStates = useMemo((): StepState[] => {
    // First pass: compute completed status for each step
    const completedStatuses = steps.map((step, index) => {
      const isSkipped = skippedSteps.includes(index);
      const isPreviousStep = index < effectiveStepIndex;
      // A step is completed if:
      // 1. The server says it's completed (action status), OR
      // 2. It's a previous step that wasn't skipped (user must have completed it to advance)
      return step.isCompleted || (isPreviousStep && !isSkipped);
    });

    // Second pass: compute full state including clickability
    return steps.map((step, index) => {
      const isCurrent = index === effectiveStepIndex;
      const isSkipped = skippedSteps.includes(index);
      const isPreviousStep = index < effectiveStepIndex;
      const isCompleted = completedStatuses[index];

      // A step is clickable if:
      // 1. It's a previous step, OR
      // 2. It's completed, OR
      // 3. It's skipped, OR
      // 4. All previous steps are completed or skipped (can advance to next step)
      const allPreviousStepsHandled =
        index === 0 ||
        steps
          .slice(0, index)
          .every(
            (_, prevIndex) =>
              completedStatuses[prevIndex] || skippedSteps.includes(prevIndex)
          );
      const isClickable =
        isPreviousStep || isCompleted || isSkipped || allPreviousStepsHandled;

      return {
        index,
        step,
        isCompleted,
        isCurrent,
        isSkipped,
        isClickable,
      };
    });
  }, [steps, effectiveStepIndex, skippedSteps]);

  return {
    effectiveStepIndex,
    skippedSteps,
    currentStep,
    isLastStep,
    canSkip,
    stepStates,
  };
}
